import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { caseStudies, getCaseStudyBySlug } from "@/data/caseStudies";
import { FadeIn, MaskedReveal } from "@/components/Reveal";
import { JsonLd, breadcrumbSchema } from "@/components/JsonLd";

export const CaseStudiesList = () => {
  return (
    <>
      <Helmet>
        <title>Case Studies — Security Operations in Quebec | Quebec Security Services</title>
        <meta name="description" content="Real Quebec security operations: Fermont mining perimeter, downtown Montreal corporate towers, and provincial alarm response. Documented metrics, client outcomes, and operational detail." />
      </Helmet>

      <section className="px-6 md:px-10 pt-24 md:pt-32 pb-16 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-amber-500" />
          Case Studies · {caseStudies.length}
        </div>
        <h1 className="font-display uppercase font-black text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] text-white max-w-5xl">
          <MaskedReveal>
            <span>Real operations.</span>
            <span className="text-amber-500">Real results.</span>
          </MaskedReveal>
        </h1>
      </section>

      <section className="px-6 md:px-10 pb-24 max-w-[1600px] mx-auto">
        <div className="space-y-16">
          {caseStudies.map((cs, i) => (
            <FadeIn key={cs.slug} delay={i * 0.05}>
              <Link
                to={`/case-studies/${cs.slug}`}
                data-testid={`case-study-card-${cs.slug}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-white/10 pt-10 hover:border-amber-500 transition-colors"
              >
                <div className="md:col-span-5 relative aspect-[4/3] overflow-hidden">
                  <img src={cs.hero_image} alt={cs.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" style={{ transitionProperty: "transform" }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute top-4 left-4 font-mono text-[11px] uppercase tracking-[0.3em] bg-amber-500 text-black px-2.5 py-1">
                    / {cs.number}
                  </div>
                </div>
                <div className="md:col-span-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500 mb-3">{cs.industry} · {cs.location}</div>
                  <h2 className="font-display uppercase font-black text-4xl md:text-5xl tracking-tight leading-tight text-white group-hover:text-amber-500 transition-colors">
                    {cs.title}
                  </h2>
                  <p className="mt-6 text-lg text-zinc-300 max-w-2xl">{cs.tagline}</p>
                  <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {cs.stats.map((s) => (
                      <div key={s.v} className="border-t border-white/10 pt-4">
                        <div className="font-display font-black text-2xl md:text-3xl text-white group-hover:text-amber-500 transition-colors">{s.k}</div>
                        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{s.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>
    </>
  );
};

export const CaseStudyDetail = () => {
  const { slug } = useParams();
  const cs = getCaseStudyBySlug(slug);
  if (!cs) return <Navigate to="/case-studies" replace />;

  return (
    <>
      <Helmet>
        <title>{`${cs.title} — Case Study | Quebec Security Services`}</title>
        <meta name="description" content={cs.tagline} />
      </Helmet>
      <JsonLd data={breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Case Studies", path: "/case-studies" },
        { name: cs.title, path: `/case-studies/${cs.slug}` },
      ])} />

      {/* Hero */}
      <section className="relative min-h-[80vh] w-full overflow-hidden bg-black flex items-end">
        <img src={cs.hero_image} alt={cs.title} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-10 py-20 md:py-24">
          <Link to="/case-studies" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-500 transition-colors mb-8">
            <ArrowLeft className="w-3 h-3" /> All case studies
          </Link>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-8">
            <span className="w-8 h-px bg-amber-500" />
            Case File / {cs.number} · {cs.industry}
          </div>
          <h1 className="font-display uppercase font-black text-5xl md:text-7xl lg:text-[9vw] tracking-tight leading-[0.88] text-white max-w-5xl">
            <MaskedReveal>
              {cs.title.split(" ").map((w, i) => <span key={i}>{w}&nbsp;</span>)}
            </MaskedReveal>
          </h1>
          <FadeIn delay={0.2}>
            <p className="mt-8 text-xl md:text-2xl text-amber-500 max-w-3xl font-display uppercase tracking-tight">{cs.tagline}</p>
          </FadeIn>
        </div>
      </section>

      {/* Meta bar */}
      <section className="border-b border-white/10 bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Meta label="Client" value={cs.client} />
          <Meta label="Location" value={cs.location} />
          <Meta label="Duration" value={cs.duration} />
          <Meta label="Scope" value={cs.scope} />
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-white/10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 md:grid-cols-4">
          {cs.stats.map((s, i) => (
            <div key={i} className="px-6 py-10 border-r border-white/10 last:border-r-0 md:border-b-0 border-b group hover:bg-amber-500 hover:text-black transition-colors duration-500">
              <div className="font-display font-black text-4xl md:text-5xl tracking-tight text-white group-hover:text-black">{s.k}</div>
              <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 group-hover:text-black/70">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="px-6 md:px-10 py-24 max-w-[1000px] mx-auto">
        <FadeIn>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber-500 mb-4">→ Challenge</div>
          <h2 className="font-display uppercase font-black text-3xl md:text-5xl tracking-tight leading-tight text-white">The problem.</h2>
          <p className="mt-6 text-lg text-zinc-300 leading-relaxed">{cs.challenge}</p>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div className="mt-16 font-mono text-[11px] uppercase tracking-[0.3em] text-amber-500 mb-4">→ Solution</div>
          <h2 className="font-display uppercase font-black text-3xl md:text-5xl tracking-tight leading-tight text-white">What we built.</h2>
          <p className="mt-6 text-lg text-zinc-300 leading-relaxed">{cs.solution}</p>
        </FadeIn>

        <FadeIn delay={0.15}>
          <div className="mt-16 font-mono text-[11px] uppercase tracking-[0.3em] text-amber-500 mb-4">→ Outcome</div>
          <h2 className="font-display uppercase font-black text-3xl md:text-5xl tracking-tight leading-tight text-white">The result.</h2>
          <p className="mt-6 text-lg text-zinc-300 leading-relaxed">{cs.outcome}</p>
        </FadeIn>

        <FadeIn delay={0.2}>
          <blockquote className="mt-20 border-l-2 border-amber-500 pl-8 py-2">
            <p className="font-display uppercase text-2xl md:text-4xl tracking-tight leading-tight text-white">{cs.quote}</p>
            <footer className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500">— {cs.quote_author}</footer>
          </blockquote>
        </FadeIn>
      </section>

      {/* Gallery */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber-500 mb-6">→ Field imagery</div>
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {cs.gallery.map((img, i) => {
            const layout = ["col-span-12 md:col-span-8 aspect-[16/10]", "col-span-12 md:col-span-4 aspect-[4/5]", "col-span-12 aspect-[21/9]"];
            return (
              <FadeIn key={i} delay={i * 0.05} className={layout[i]}>
                <div className="relative w-full h-full overflow-hidden group">
                  <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ transitionProperty: "transform" }} />
                  <div className="absolute top-3 left-3 w-6 h-6 border-t border-l border-amber-500" />
                  <div className="absolute top-3 right-3 w-6 h-6 border-t border-r border-amber-500" />
                  <div className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-amber-500" />
                  <div className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-amber-500" />
                </div>
              </FadeIn>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-amber-500 text-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] mb-4">→ Ready to deploy?</div>
              <h3 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9]">
                Build your own<br />case file.
              </h3>
            </div>
            <Link to="/contact" className="group inline-flex items-center gap-4 bg-black text-amber-500 h-14 px-6 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-zinc-900 transition-colors self-start">
              Contact us
              <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

const Meta = ({ label, value }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-2">◉ {label}</div>
    <div className="text-white text-sm">{value}</div>
  </div>
);
